import { ChangeDetectorRef, Component, HostListener, Injector, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Chip, Tag } from 'src/api/v3/common';
import { AuthService } from 'src/app/api/auth.service';
import { TagService } from 'src/app/api/tag.service';
import { UserService } from 'src/app/api/user.service';
import { LanguageAware } from 'src/app/general/language-aware';
import { UResolvable } from 'src/app/guards/UResolvable';
import { ItemConfiguration } from '../../components/company-columns/company-columns.component';
import { DataTableGetter } from '../../components/data-table/data-table.component';
import { FilterCriteria } from '../company-users/company-users.component';

@Component({
  selector: 'app-tag-list-desktop',
  templateUrl: './tag-list-desktop.component.html',
  styleUrls: ['./tag-list-desktop.component.scss']
})
export class TagListDesktopComponent extends LanguageAware implements OnInit, UResolvable<typeof TagListDesktopComponent> {
  public canAdd = this.us.currentUser.permissions?.permissions.tags.create && (!this.us.currentUser.belongsToCompany || this.us.currentUser.belongsToCompany.active);
  public canDelete = this.us.currentUser.permissions?.permissions.tags.delete && (!this.us.currentUser.belongsToCompany || this.us.currentUser.belongsToCompany.active);
  public filterCriteria: FilterCriteria = { searchPhrase: '', searchFields: [], currentPage: 0, lastPage: 0 };
  filterChips: Chip[];
  public searchVisible = false;
  public isSearchLoading = false;
  private isManuallySubmitted = false;
  private typingTimer = null;
  private doneTypingInterval = 500;
  private resultsFiltered = false;
  public columns = this.persistenceService.bind('tag_list_columns', [
    'name',
    'description',
    this.us.currentUser.company_id === 0 ? 'company' : null,
    this.canDelete ? 'fake:delete' : null
  ]);
  public readonly columnConfig: ItemConfiguration<string>[] = [
    { name: this.trans('tags.labels.name'), value: 'name' },
    { name: this.trans('tags.labels.description'), value: 'description' },
    this.us.currentUser.company_id === 0 ? { name: this.trans('users.labels.company'), value: 'company' } : null,
    this.canDelete ? { name: this.trans('general.delete'), value: 'fake:delete' } : null,
  ];
  loader: DataTableGetter<Tag>;

  constructor(
    cdRef: ChangeDetectorRef
  ) {
    super(cdRef);
    this.loader = this.tagService.getTagsGetter(null);
    this.filterChips = [
      { name: 'name', label: this.trans('tags.labels.name'), value: false, modifiableColumn: true },
      { name: 'company', label: this.trans('users.labels.company'), value: false, modifiableColumn: true },
      { name: 'description', label: this.trans('tags.labels.description'), value: false, modifiableColumn: true },
    ];
  }

  ngOnInit(): void {
  }

  private resetFilterCriteria(): void {
    this.filterCriteria = {
      searchPhrase: '',
      searchFields: [],
      currentPage: 0,
      lastPage: -1,
    };
    this.filterChips.map(chip => chip.value = false);
  }

  public hideSearch(): void {
    if(!this.searchVisible) { return; }
    this.searchVisible = false;
    this.isSearchLoading = false;
    this.resetFilterCriteria();
    if(this.resultsFiltered) {
      this.loader = this.tagService.getTagsGetter(this.filterCriteria);
      this.resultsFiltered = false;
    };
    const searchInputField = document.getElementById('searchInputField');
    searchInputField.blur();
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.hideSearch();
  }

  public showSearch(): void {
    this.searchVisible = true;
  }

  public onChipPress(event: any, chip: Chip): void {
    if(this.filterCriteria.searchFields.includes(chip.name) && !chip.value) {
      const index = this.filterCriteria.searchFields.indexOf(chip.name);
      this.filterCriteria.searchFields.splice(index, 1);
    } else if (!this.filterCriteria.searchFields.includes(chip.name) && chip.value) {
      this.filterCriteria.searchFields.push(chip.name);
    }
    this.onSearchSubmit(event);
  };

  public onSearchSubmit(event: any): void {
    clearTimeout(this.typingTimer);
    if(event.key === 'Enter' || event.type === 'click') {
      this.isManuallySubmitted = true;
      this.initializeSearch();
    } else {
      this.isManuallySubmitted = false;
      this.typingTimer = setTimeout(() => {
        this.initializeSearch();
      }, this.doneTypingInterval);
    }
  }

  private async initializeSearch(): Promise<void> {
    this.resetPagesCount();
    if(this.filterCriteria.searchPhrase.length >= (this.isManuallySubmitted ? 2 : 3)) {
      this.isSearchLoading = true;
      await this.setFilteredCompanies().then(() => {
        this.resultsFiltered = true;
        this.isSearchLoading = false;
      });
    } else if (this.resultsFiltered && this.filterCriteria.searchPhrase.length === 0) {
      this.loader = this.tagService.getTagsGetter(this.filterCriteria);
      this.resultsFiltered = false;
    }
  }

  private setFilteredCompanies(): Promise<void> {
    this.filterActiveSearchFields();
    return new Promise((resolve) => {
      this.loader = this.tagService.getTagsGetter(this.filterCriteria);
      resolve();
    });
  }

  private filterActiveSearchFields(): void {
    this.filterCriteria.searchFields.map(searchFieldName => {
      if (!this.columns.value.includes(searchFieldName)) {
        const index = this.filterCriteria.searchFields.indexOf(searchFieldName);
        this.filterCriteria.searchFields.splice(index, 1);
        const inactiveChip = this.filterChips.find(chip => chip.name === searchFieldName);
        inactiveChip.value = false;
      }
    });
  }

  private resetPagesCount(): void {
    this.filterCriteria.currentPage = 0;
    this.filterCriteria.lastPage = 0;
  }

  public static async resolve(injector: Injector, route: ActivatedRouteSnapshot, param?: string): Promise<void> {
    const user = injector.get(UserService);
    if (!user.currentUser) {
      const auth = injector.get(AuthService);
      await auth.loadUserData();
    }
    const tagService = injector.get(TagService);
    if ( tagService.tags.size === 0 ) {
      await tagService.loadAllTags();
    }

    const tagId = parseInt(param, 10);
    if ( tagId && !isNaN(tagId) ) {
      const found = tagService.tags.get(tagId);
      if ( !found ) {
        await tagService.loadTagById(tagId);
      }
    }
  }

}
